import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaFileInvoice, FaDownload, FaPlus, FaCreditCard, FaReceipt } from 'react-icons/fa';
import ReceiptButton from '../components/receipt/ReceiptButton';
import TotalAmountCard from '../components/common/TotalAmountCard';
import { usePageTitle } from '../context/PageTitleContext';
import './Billing.scss';

function Billing() {
  const { user } = useSelector(state => state.auth);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('invoices');
  const { setPageTitle } = usePageTitle();

  // Set page title when component mounts
  useEffect(() => {
    setPageTitle("Billing & Payments");
  }, [setPageTitle]);

  useEffect(() => {
    fetchInvoices();
  }, [user]);

  const fetchInvoices = async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    try {
      // Get completed orders (these become invoices)
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', user.uid),
        where('status', '==', 'completed')
      );
      
      const querySnapshot = await getDocs(q);
      const invoicesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by date (newest first)
      invoicesData.sort((a, b) => {
        const dateA = new Date(a.paidAt || a.createdAt);
        const dateB = new Date(b.paidAt || b.createdAt);
        return dateB - dateA;
      });
      
      setInvoices(invoicesData);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateInvoiceNumber = (invoiceId) => {
    return `INV-${invoiceId.slice(-6).toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="billing-page">
      <div className="billing-tabs">
        <button 
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          Invoices
        </button>
        <button 
          className={`tab ${activeTab === 'payment-methods' ? 'active' : ''}`}
          onClick={() => setActiveTab('payment-methods')}
        >
          Payment Methods
        </button>
      </div>
      
      {activeTab === 'invoices' && (
        <div className="invoices-section">
          {!loading && invoices.length > 0 && (
            <div className="billing-summary">
              <TotalAmountCard orders={invoices} title="Total Paid" />
            </div>
          )}
        
          {loading ? (
            <div className="loading">Loading your invoices...</div>
          ) : invoices.length > 0 ? (
            <div className="invoices-container">
              <div className="invoices-header">
                <div className="col">Invoice</div>
                <div className="col">Date</div>
                <div className="col">Service</div>
                <div className="col">Amount</div>
                <div className="col">Status</div>
                <div className="col actions">Actions</div>
              </div>
              
              <div className="invoices-list">
                {invoices.map(invoice => (
                  <div key={invoice.id} className="invoice-row">
                    <div className="col">
                      <div className="invoice-number">
                        <FaFileInvoice className="icon" />
                        <span>{generateInvoiceNumber(invoice.id)}</span>
                      </div>
                    </div>
                    <div className="col">
                      {formatDate(invoice.paidAt || invoice.createdAt)}
                    </div>
                    <div className="col service-name">
                      {invoice.packageTitle}
                    </div>
                    <div className="col amount">
                      ${invoice.totalPrice}
                    </div>
                    <div className="col">
                      <span className="status paid">Paid</span>
                    </div>
                    <div className="col actions">
                      {invoice.receipt_url ? (
                        <a 
                          href={invoice.receipt_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="btn-icon" 
                          title="View Receipt"
                        >
                          <FaReceipt />
                        </a>
                      ) : invoice.status === 'completed' ? (
                        <ReceiptButton 
                          sessionId={invoice.sessionId || ''}
                          className="btn-receipt"
                          buttonText={<FaReceipt />}
                        />
                      ) : (
                        <button className="btn-icon" title="Download Invoice">
                          <FaDownload />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <h3>No Invoices</h3>
              <p>You don&apos;t have any invoices yet.</p>
              <a href="/packages" className="btn btn-primary">Browse Packages</a>
            </div>
          )}
        </div>
      )}
      
      {activeTab === 'payment-methods' && (
        <div className="payment-methods-section">
          <div className="payment-methods-header">
            <h2>Your Payment Methods</h2>
            <button className="btn btn-primary add-method">
              <FaPlus className="icon" /> Add Payment Method
            </button>
          </div>
          
          <div className="empty-payment-methods">
            <div className="icon-container">
              <FaCreditCard className="icon" />
            </div>
            <h3>No Payment Methods</h3>
            <p>You haven&apos;t added any payment methods yet.</p>
            <p className="info">Adding a payment method will make checkout faster.</p>
            <button className="btn btn-primary">
              <FaPlus className="icon" /> Add Payment Method
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Billing; 